import { useEffect, useRef, useCallback } from 'react';
import { useRealtime } from '../contexts/RealtimeContext';
import api from '../lib/api';
import { toast } from 'sonner';

// ============================================================
// Web Audio API - Sound Engine with 12 built-in sounds + custom
// ============================================================
const createAudioContext = () => {
  try {
    return new (window.AudioContext || window.webkitAudioContext)();
  } catch { return null; }
};

const ensureCtx = (ref) => {
  if (!ref.current) ref.current = createAudioContext();
  if (ref.current?.state === 'suspended') ref.current.resume();
  return ref.current;
};

const beep = (ctx, freq, start, dur, vol, wave = 'sine') => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination);
  o.type = wave;
  o.frequency.value = freq;
  g.gain.setValueAtTime(vol, start);
  g.gain.exponentialRampToValueAtTime(0.001, start + dur);
  o.start(start); o.stop(start + dur);
};

const playUrgent = (ctx, vol) => { const t = ctx.currentTime; beep(ctx, 880, t, 0.3, vol); beep(ctx, 880, t + 0.25, 0.3, vol); beep(ctx, 880, t + 0.5, 0.3, vol); };
const playNormal = (ctx, vol) => { const t = ctx.currentTime; beep(ctx, 660, t, 0.25, vol); beep(ctx, 660, t + 0.35, 0.25, vol); };
const playSubtle = (ctx, vol) => { beep(ctx, 520, ctx.currentTime, 0.2, vol); };

const playSiren = (ctx, vol) => {
  const t = ctx.currentTime; const o = ctx.createOscillator(); const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination); o.type = 'sawtooth';
  o.frequency.setValueAtTime(400, t); o.frequency.linearRampToValueAtTime(900, t + 0.5);
  o.frequency.linearRampToValueAtTime(400, t + 1.0); o.frequency.linearRampToValueAtTime(900, t + 1.5);
  g.gain.setValueAtTime(vol * 0.6, t); g.gain.exponentialRampToValueAtTime(0.001, t + 1.8);
  o.start(t); o.stop(t + 1.8);
};

const playChime = (ctx, vol) => {
  const t = ctx.currentTime;
  [523, 659, 784].forEach((freq, i) => {
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.type = 'sine'; o.frequency.value = freq;
    const s = t + i * 0.2; g.gain.setValueAtTime(vol * 0.8, s); g.gain.exponentialRampToValueAtTime(0.001, s + 0.6);
    o.start(s); o.stop(s + 0.6);
  });
};

const playRadar = (ctx, vol) => {
  const t = ctx.currentTime; const o = ctx.createOscillator(); const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination); o.type = 'sine';
  o.frequency.setValueAtTime(200, t); o.frequency.exponentialRampToValueAtTime(1200, t + 0.4); o.frequency.exponentialRampToValueAtTime(200, t + 0.8);
  g.gain.setValueAtTime(vol * 0.7, t); g.gain.setValueAtTime(vol * 0.5, t + 0.4); g.gain.exponentialRampToValueAtTime(0.001, t + 1.0);
  o.start(t); o.stop(t + 1.0);
};

const playEmergency = (ctx, vol) => { const t = ctx.currentTime; for (let i = 0; i < 6; i++) beep(ctx, i % 2 === 0 ? 1000 : 700, t + i * 0.18, 0.16, vol * 0.8, 'square'); };
const playRising = (ctx, vol) => { const t = ctx.currentTime; [440, 554, 659].forEach((f, i) => beep(ctx, f, t + i * 0.18, 0.22, vol * 0.8)); };
const playFalling = (ctx, vol) => { const t = ctx.currentTime; [659, 554, 440].forEach((f, i) => beep(ctx, f, t + i * 0.18, 0.22, vol * 0.8)); };

const playHorn = (ctx, vol) => {
  const t = ctx.currentTime; const o1 = ctx.createOscillator(); const o2 = ctx.createOscillator(); const g = ctx.createGain();
  o1.connect(g); o2.connect(g); g.connect(ctx.destination);
  o1.type = 'sawtooth'; o1.frequency.value = 220; o2.type = 'sawtooth'; o2.frequency.value = 277;
  g.gain.setValueAtTime(vol * 0.7, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
  o1.start(t); o2.start(t); o1.stop(t + 0.6); o2.stop(t + 0.6);
};

const playPing = (ctx, vol) => {
  const t = ctx.currentTime; const o = ctx.createOscillator(); const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination); o.type = 'sine';
  o.frequency.setValueAtTime(1800, t); o.frequency.exponentialRampToValueAtTime(1200, t + 0.1);
  g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
  o.start(t); o.stop(t + 0.15);
};

const playTrill = (ctx, vol) => { const t = ctx.currentTime; for (let i = 0; i < 8; i++) beep(ctx, i % 2 === 0 ? 1047 : 1175, t + i * 0.06, 0.06, vol * 0.7); };

const playCustomSound = (url, volume) => {
  try { const a = new Audio(url); a.volume = Math.max(0, Math.min(1, volume / 100)); a.play().catch(() => {}); } catch {}
};

const SOUND_PLAYERS = {
  urgent: playUrgent, normal: playNormal, subtle: playSubtle, siren: playSiren,
  chime: playChime, radar: playRadar, emergency: playEmergency, rising: playRising,
  falling: playFalling, horn: playHorn, ping: playPing, trill: playTrill,
};

// Import extended library players
let extendedPlayers = null;
const getExtendedPlayers = () => {
  if (!extendedPlayers) {
    try {
      const lib = require('../lib/sound-library');
      extendedPlayers = lib.SOUND_PLAYERS;
    } catch { extendedPlayers = {}; }
  }
  return extendedPlayers;
};

export const playSound = (audioCtxRef, soundType, volume, customSoundUrl) => {
  if (soundType?.startsWith('custom:') || customSoundUrl) {
    playCustomSound(customSoundUrl || soundType.replace('custom:', ''), volume);
    return;
  }
  const ctx = ensureCtx(audioCtxRef);
  if (!ctx) return;
  const vol = (volume / 100) * 0.3;
  // Try original 12 sounds first, then extended library
  const player = SOUND_PLAYERS[soundType] || getExtendedPlayers()[soundType];
  if (player) player(ctx, vol); else playSubtle(ctx, vol);
};

// ============================================================
// Event mapping
// ============================================================
const EVENT_TYPE_MAP = {
  new_ticket: 'new_ticket',
  ticket_update: 'ticket_status_change',
  ticket_assigned: 'ticket_assigned',
  push_notification: 'push_notification',
  stock_change: 'stock_receipt',
  pm_change: 'pm_created',
  material_request: 'material_request_created',
};

const EVENT_TITLES = {
  new_ticket: 'Tichet Nou', ticket_update: 'Status Tichet Schimbat',
  ticket_assigned: 'Tichet Asignat', push_notification: 'Notificare',
  stock_change: 'Modificare Stoc', pm_change: 'Actualizare Mentenanta',
  material_request: 'Cerere Material',
};
const ACTION_TITLES = {
  accepted: 'Tichet Acceptat', assigned: 'Tichet Asignat',
  status_change: 'Status Tichet Schimbat', completed: 'Tichet Finalizat',
  receipt: 'Receptie Marfa', transfer: 'Transfer Stoc', consumption: 'Consum Stoc',
  created: 'Element Creat', approved: 'Cerere Aprobata', rejected: 'Cerere Respinsa',
};

// ============================================================
// Hook - subscribes to RealtimeContext events
// ============================================================
export const useAlertSound = () => {
  const { subscribe } = useRealtime();
  const audioCtxRef = useRef(null);
  const settingsRef = useRef(null);

  // Fetch settings once
  useEffect(() => {
    api.get('/alert-settings').then(res => { settingsRef.current = res.data; }).catch(() => {
      settingsRef.current = {
        sound_enabled: true, volume: 80, desktop_notifications: true,
        events: {
          new_ticket: { enabled: true, sound_type: 'urgent' },
          ticket_assigned: { enabled: true, sound_type: 'chime' },
          ticket_status_change: { enabled: true, sound_type: 'ping' },
          ticket_comment: { enabled: false, sound_type: 'subtle' },
        },
      };
    });
  }, []);

  // Request desktop notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
  }, []);

  // Init audio context on user interaction
  useEffect(() => {
    const init = () => { if (!audioCtxRef.current) audioCtxRef.current = createAudioContext(); document.removeEventListener('click', init); document.removeEventListener('keydown', init); };
    document.addEventListener('click', init); document.addEventListener('keydown', init);
    return () => { document.removeEventListener('click', init); document.removeEventListener('keydown', init); };
  }, []);

  const showDesktopNotif = useCallback((title, body, tag) => {
    if (!settingsRef.current?.desktop_notifications) return;
    if ('Notification' in window && Notification.permission === 'granted') {
      try { const n = new Notification(title, { body, icon: '/favicon.ico', tag: tag || 'maintix-alert' }); setTimeout(() => n.close(), 8000); } catch {}
    }
  }, []);

  const playAlertForEvent = useCallback((eventType, info) => {
    const settings = settingsRef.current;
    if (!settings?.sound_enabled) return;
    const eventKey = EVENT_TYPE_MAP[eventType] || eventType;
    const eventConfig = settings.events?.[eventKey];
    if (!eventConfig?.enabled) return;
    playSound(audioCtxRef, eventConfig.sound_type || 'subtle', settings.volume, eventConfig.custom_sound_url || null);
    showDesktopNotif(info?.title || EVENT_TITLES[eventType] || 'Alerta', info?.message || '', info?.id);
  }, [showDesktopNotif]);

  // Subscribe to all relevant events
  useEffect(() => {
    const unsubs = [];

    unsubs.push(subscribe('new_ticket', (msg) => {
      const d = msg.data || {};
      const isUrgent = ['critical', 'urgent'].includes(d.priority);
      playAlertForEvent('new_ticket', {
        title: `Tichet Nou${isUrgent ? ' - URGENT' : ''} #${d.number || ''}`,
        message: `${d.title || 'Tichet nou creat'}\n${d.equipment_name || ''}${d.created_by_name ? `\nCreat de: ${d.created_by_name}` : ''}`,
        id: d.id
      });
      // Toast is NOT shown here - AlertSplashScreen handles the visual notification
    }));

    unsubs.push(subscribe('ticket_update', (msg) => {
      const action = msg.action || 'status_change';
      const eventType = action === 'assigned' ? 'ticket_assigned' : 'ticket_update';
      const d = msg.data || {};
      playAlertForEvent(eventType, {
        title: ACTION_TITLES[action] || 'Actualizare Tichet',
        message: `#${d.number || ''} ${d.title || ''} - ${d.status || ''}${d.assigned_to_name ? `\nAsignat: ${d.assigned_to_name}` : ''}`,
        id: d.id
      });
      // Toast is NOT shown here - AlertSplashScreen handles the visual notification
    }));

    unsubs.push(subscribe('stock_change', (msg) => {
      const d = msg.data || {};
      toast.info(msg.action_label || 'Modificare Stoc', {
        description: `${d.article_name || d.name || 'Articol'} - ${msg.action || 'modificat'}`,
        duration: 4000,
      });
    }));

    unsubs.push(subscribe('pm_change', (msg) => {
      const d = msg.data || {};
      toast.info(msg.action_label || 'Actualizare Mentenanta', {
        description: `${d.equipment_name || d.title || ''} - ${msg.action || 'actualizat'}`,
        duration: 4000,
      });
    }));

    unsubs.push(subscribe('material_request', (msg) => {
      const d = msg.data || {};
      toast.info(msg.action_label || 'Cerere Material', {
        description: `${d.title || d.article_name || ''} - ${msg.action || 'actualizata'}`,
        duration: 4000,
      });
    }));

    unsubs.push(subscribe('push_notification', (msg) => {
      const d = msg.data || {};
      playAlertForEvent('push_notification', { title: d.title || 'Notificare', message: d.message || '', id: d.id });
    }));

    return () => unsubs.forEach(fn => fn());
  }, [subscribe, playAlertForEvent]);
};

export default useAlertSound;
